import { Button } from "components/Button";
import { Card, CardHeader } from "components/Card";
import { Input } from "components/Input";
import { UserContext } from "context/UserContext";
import React, { useContext } from "react";
import { useLoaderData, useNavigate } from "react-router-dom";
import { AliErrorRecord } from "services/ALIErrorRecord";
import RecordService from "services/RecordService";
import { formatPhoneNumber, formatZipCode } from "utils/helpers";

const recordService = new RecordService("ali/error");

// Styles
const styles = {
  errorCard: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "8px",
    borderRadius: "8px",
    padding: "12px",
    gridColumn: "1 / span 2",
    border: "1px solid #ddd",
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between" as const,
    alignItems: "center" as const,
    borderBottom: "1px solid #ddd",
    padding: "8px 12px",
  },
  errorGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "16px",
    alignItems: "center" as const,
  },
  mainCard: {
    width: "100%",
    marginBottom: "20px",
  },
  cardContent: {
    padding: "20px",
    paddingBottom: "40px",
  },
  fieldsGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "25px",
  },
  fieldColumn: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "8px",
  },
  divider: {
    margin: "20px 0",
  },
  additionalInfoContainer: {
    padding: "0 20px",
  },
  additionalInfoHeader: {
    marginBottom: "20px",
    textAlign: "center" as const,
  },
  additionalInfoTitle: {
    marginBottom: "10px",
  },
  expandableContent: {
    overflow: "hidden",
    transition: "max-height 0.5s cubic-bezier(0.4,0,0.2,1), opacity 0.5s",
  },
  additionalInfoGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "100px",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "center" as const,
  },
  closeButtonContainer: {
    display: "flex",
    justifyContent: "flex-end" as const,
    marginTop: "20px",
  },
};

// Data loader
export const loadRecord = async ({ params }): Promise<AliErrorRecord> => {
  try {
    const res = await recordService.getRecords(
      {
        criterias: [{ field: "a.idx", type: "=", value: params.idx }],
      },
      "searchByCriteria",
    );

    if (res.status === 401) {
      throw new Error("Unable to fetch error record");
    }

    if (!res.data?.message?.list?.length) {
      throw new Error("Error record not found");
    }

    const record = res.data.message.list[0];
    const errorDescription =
      await recordService.getErrorDescriptionByErrorNumber(record.error);

    if (errorDescription) {
      record.errorDescription = errorDescription;
    }

    return record;
  } catch (error: Error | any) {
    throw new Error(error.message || "Failed to load error record");
  }
};

// Components
const ErrorHeader = ({ record }: { record: AliErrorRecord }) => (
  <Card style={styles.errorCard}>
    <CardHeader style={styles.cardHeader}>Error List</CardHeader>
    <div style={styles.errorGrid}>
      <Input
        name="error"
        label="Error Number"
        value={record.error ? String(record.error) : ""}
        readonly
        scale="m"
        labelSize="m"
      />
      <Input
        name="errorDescription"
        label="Error Description"
        value={record.errorDescription || ""}
        readonly
        scale="m"
        labelSize="m"
      />
    </div>
  </Card>
);

const PrimaryFields = ({ record }: { record: AliErrorRecord }) => (
  <div style={styles.fieldColumn}>
    <Input
      name="cpn"
      label="Telephone Number"
      value={formatPhoneNumber(record.CPN) || ""}
      readonly
      scale="s"
      labelSize="s"
    />
    <Input
      name="nam"
      label="Customer Name"
      value={record.NAM || ""}
      readonly
      scale="s"
      labelSize="s"
    />
    <Input
      name="state"
      label="State/Province"
      value={record.STA || ""}
      readonly
      scale="s"
      labelSize="s"
    />
    <Input
      name="mcn"
      label="MSAG Community Name"
      value={record.MCN || ""}
      readonly
      scale="s"
      labelSize="s"
    />
    <Input
      name="stn"
      label="Street Name"
      value={record.STN || ""}
      readonly
      scale="s"
      labelSize="s"
    />
    <Input
      name="sts"
      label="Street Suffix"
      value={record.STS || ""}
      readonly
      scale="s"
      labelSize="s"
    />
    <Input
      name="prd"
      label="Prefix Directional"
      value={record.PRD || ""}
      readonly
      scale="s"
      labelSize="s"
    />
    <Input
      name="pod"
      label="Post Directional"
      value={record.POD || ""}
      readonly
      scale="s"
      labelSize="s"
    />
    <Input
      name="hno"
      label="House Number"
      value={record.HNO || ""}
      readonly
      scale="s"
      labelSize="s"
    />
    <Input
      name="hns"
      label="House Number Suffix"
      value={record.HNS || ""}
      readonly
      scale="s"
      labelSize="s"
    />
    <Input
      name="loc"
      label="Location"
      value={record.LOC || ""}
      readonly
      scale="s"
      labelSize="s"
    />
  </div>
);

const SecondaryFields = ({ record }: { record: AliErrorRecord }) => (
  <div style={styles.fieldColumn}>
    <Input
      name="foc"
      label="Function of Change"
      value={record.FOC || ""}
      readonly
      scale="s"
      labelSize="s"
    />
    <Input
      name="cpf"
      label="NENA ID"
      value={record.CPF || ""}
      readonly
      scale="s"
      labelSize="s"
    />
    <Input
      name="lecCOI"
      label="County ID"
      value={record.lecCOI || ""}
      readonly
      scale="s"
      labelSize="s"
    />
    <Input
      name="cls"
      label="Class of Service"
      value={record.CLS || ""}
      readonly
      scale="s"
      labelSize="s"
    />
    <Input
      name="tys"
      label="Type of Service"
      value={record.TYS || ""}
      readonly
      scale="s"
      labelSize="s"
    />
    <Input
      name="comments"
      label="Comments"
      value={record.Comments || ""}
      readonly
      scale="s"
      labelSize="s"
    />
    <Input
      name="lecZipCode"
      label="Zip Code"
      value={formatZipCode(record.lecZipCode) || ""}
      readonly
      scale="s"
      labelSize="s"
    />
    <Input
      name="lecZipExtension"
      label="Zip Extension"
      value={record.lecZipExtension || ""}
      readonly
      scale="s"
      labelSize="s"
    />
    <Input
      name="areaCode"
      label="Area Code of Main Number"
      value={record.MainNPA || ""}
      readonly
      scale="s"
      labelSize="s"
    />
    <Input
      name="mainNumber"
      label="Main Number"
      value={record.MainNumber || ""}
      readonly
      scale="s"
      labelSize="s"
    />
    <Input
      name="extractDate"
      label="Extract Date"
      value={record.ExtractDate || ""}
      readonly
      scale="s"
      labelSize="s"
    />
  </div>
);

const AdditionalFieldsLeft = ({ record }: { record: AliErrorRecord }) => (
  <div style={styles.fieldColumn}>
    <Input
      name="exchange"
      label="Exchange"
      value={record.Exchange || ""}
      readonly
      scale="s"
      labelSize="s"
    />
    <Input
      name="nenaReserved"
      label="NENA Reserved"
      value={record.NENAReserved || ""}
      readonly
      scale="s"
      labelSize="s"
    />
    <Input
      name="lecESN"
      label="Emergency Service Number"
      value={record.lecESN || ""}
      readonly
      scale="s"
      labelSize="s"
    />
    <Input
      name="orderNumber"
      label="Order Number"
      value={record.OrderNumber || ""}
      readonly
      scale="s"
      labelSize="s"
    />
    <Input
      name="generalUse"
      label="General Use"
      value={record.GeneralUse || ""}
      readonly
      scale="s"
      labelSize="s"
    />
    <Input
      name="lecLat"
      label="Latitude"
      value={record.lecLAT || ""}
      readonly
      scale="s"
      labelSize="s"
    />
    <Input
      name="lecLong"
      label="Longitude"
      value={record.lecLON || ""}
      readonly
      scale="s"
      labelSize="s"
    />
    <Input
      name="lecELV"
      label="Elevation"
      value={record.lecELV || ""}
      readonly
      scale="s"
      labelSize="s"
    />
    <Input
      name="nenaReserved"
      label="NENA Reserved"
      value={record.NENAReserved || ""}
      readonly
      scale="s"
      labelSize="s"
    />
    <Input
      name="dataProviderId"
      label="Data Provider ID"
      value={record.DataProviderID || ""}
      readonly
      scale="s"
      labelSize="s"
    />
    <Input
      name="reserved2"
      label="Reserved 2"
      value={record.Reserved2 || ""}
      readonly
      scale="s"
      labelSize="s"
    />
    <Input
      name="customerCode"
      label="Customer Code"
      value={record.CustomerCode || ""}
      readonly
      scale="s"
      labelSize="s"
    />
  </div>
);

const AdditionalFieldsRight = ({ record }: { record: AliErrorRecord }) => (
  <div style={styles.fieldColumn}>
    <Input
      name="file"
      label="File"
      value={record.file || ""}
      readonly
      scale="s"
      labelSize="s"
    />
    <Input
      name="aliTmpIdx"
      label="ALI Temp Index"
      value={record.ali_tmp_idx || ""}
      readonly
      scale="s"
      labelSize="s"
    />
    <Input
      name="processed"
      label="Processed"
      value={record.processed || ""}
      readonly
      scale="s"
      labelSize="s"
    />
    <Input
      name="created"
      label="Created"
      value={record.created || ""}
      readonly
      scale="s"
      labelSize="s"
    />
    <Input
      name="lastModified"
      label="Last Modified"
      value={record.last_modified || ""}
      readonly
      scale="s"
      labelSize="s"
    />
    <Input
      name="modifiedBy"
      label="Modified By"
      value={record.modified_by || ""}
      readonly
      scale="s"
      labelSize="s"
    />
  </div>
);

// Main component
export default function ALIErrorRead() {
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const record = useLoaderData() as AliErrorRecord;
  const [showAdditionalInfo, setShowAdditionalInfo] = React.useState(false);

  const handleToggleAdditionalInfo = () => {
    setShowAdditionalInfo((prev) => {
      const next = !prev;
      if (!prev) {
        setTimeout(() => {
          const exchangeInput = document.querySelector(
            'calcite-input[name="exchange"]',
          ) as any | null;
          exchangeInput?.setFocus();
        }, 100);
      }
      return next;
    });
  };

  const handleClose = () => navigate(-1);

  if (!record) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      <ErrorHeader record={record} />

      <Card style={styles.mainCard}>
        <div style={styles.cardContent}>
          <div style={styles.fieldsGrid}>
            <PrimaryFields record={record} />
            <SecondaryFields record={record} />
          </div>

          <hr style={styles.divider} />

          <div style={styles.additionalInfoContainer}>
            <div style={styles.additionalInfoHeader}>
              {showAdditionalInfo && (
                <h3 style={styles.additionalInfoTitle}>
                  Additional Information
                </h3>
              )}
            </div>

            <div
              style={{
                ...styles.expandableContent,
                maxHeight: showAdditionalInfo ? 1000 : 0,
                opacity: showAdditionalInfo ? 1 : 0,
              }}
            >
              {showAdditionalInfo && (
                <div style={styles.additionalInfoGrid}>
                  <AdditionalFieldsLeft record={record} />
                  <AdditionalFieldsRight record={record} />
                </div>
              )}
            </div>
          </div>

          <div
            style={{
              ...styles.buttonContainer,
              marginTop: showAdditionalInfo ? "20px" : "0",
            }}
          >
            <Button
              variant="outline"
              kind="brand"
              onClick={handleToggleAdditionalInfo}
            >
              {showAdditionalInfo ? "Hide" : "Show"} Additional Information
            </Button>
          </div>

          <div style={styles.closeButtonContainer}>
            <Button
              variant="outline"
              kind="brand"
              label="Close"
              onClick={handleClose}
            >
              Close
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
